import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import parse from "html-react-parser";

import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Row,
  Col,
  Card,
  CardBody,
  CardFooter,
} from "reactstrap";

import { toast, Slide } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import styles from "../../assets/preview.module.scss";

//import storageType action
import { updateStorageCategory } from "../../redux/actions/storageCategories";

//Configure toastify
toast.configure();

const StorageCategoryForm = (props) => {
  const storageCategoryDetail = props.storageCategoryDetail;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [currentStorageCategoryInfo, setCurrentStorageCategoryInfo] = useState(
    storageCategoryDetail
  );

  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [removePreviewImg, setRemovePreviewImg] = useState(false);
  const [imageErr, setImageErr] = useState("");

  //states for handling validations
  const [nameErr, setNameErr] = useState("");

  useEffect(() => {
    if (storageCategoryDetail !== undefined) {
      setCurrentStorageCategoryInfo(storageCategoryDetail); //set storage type details in the state when storageCategoryDetail changes

      //set profile image preview when userDetail changes
      if (storageCategoryDetail.image) {
        setImagePreview(
          process.env.REACT_APP_STORAGE_CATEGORY_IMAGE_URL +
            storageCategoryDetail.image
        );
      }
    }
  }, [storageCategoryDetail]);

  //input change handler
  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setCurrentStorageCategoryInfo({
      ...currentStorageCategoryInfo,
      [name]: value,
    });
  };

  //validation handler
  const handleValidation = (event) => {
    const inputValue = event.target.value.trim();

    const inputFieldName = event.target.name;

    //set error message for firstName
    if (inputFieldName === "name") {
      if (inputValue.length < 2) {
        setNameErr("Please enter atleast 2 characters!");
      } else {
        setNameErr("");
      }
    }
  };

  //file input handler
  const handleFileInput = (event) => {
    setImageErr("");

    let fileSize = 0;

    let errorCount = 0;

    const file = event.target.files[0];

    if (file) {
      fileSize = file.size / 1024;

      if (!file.name.match(/\.(jpg|jpeg|png|gif)$/i)) {
        setImageErr("Only Images are allowed! ");

        errorCount++;
      }

      //check if filesize is not more than 1MB
      if (fileSize > 1024) {
        setImageErr("Please upload a file of size less than 1MB!");

        errorCount++;
      }

      if (errorCount === 0) {
        const imageAsBase64 = URL.createObjectURL(file);

        setSelectedImage(file);

        setImagePreview(imageAsBase64);

        setRemovePreviewImg(false);
      }
    }
  };

  const removeProfilePicture = (event) => {
    event.preventDefault();

    setImagePreview("");

    setSelectedImage(null);

    setRemovePreviewImg(true);
  };

  //update form handler
  const submitHandler = (event) => {
    event.preventDefault();

    let errorCount = 0;

    const formData = new FormData();

    if (
      JSON.stringify(currentStorageCategoryInfo) === "{}" ||
      currentStorageCategoryInfo.name === "" ||
      currentStorageCategoryInfo.name === null ||
      currentStorageCategoryInfo.name.length < 2
    ) {
      setNameErr("Please enter atleast 2 characters!");

      errorCount++;
    } else {
      formData.append("name", currentStorageCategoryInfo.name);

      setNameErr("");
    }

    if (imageErr !== "") {
      errorCount++;
    } else {
      if (selectedImage !== null)
        formData.append("storageImage", selectedImage);
    }

    if (removePreviewImg) {
      formData.append("removeStorageImage", removePreviewImg);
    }

    if (errorCount > 0) {
      return;
    } else {
      //dispatch to update the storage category
      dispatch(updateStorageCategory(currentStorageCategoryInfo.id, formData))
        .then((response) => {
          setCurrentStorageCategoryInfo({ ...currentStorageCategoryInfo });

          toast("Storage category updated successfully!", {
            transition: Slide,

            closeButton: true,

            autoClose: 3000,

            position: "top-right",

            type: "success", // info/success/warning/error
          });
        })
        .catch((error) => {
          toast(error.response.data.message, {
            transition: Slide,

            closeButton: true,

            autoClose: 3000,

            position: "top-right",

            type: "error",
          });
        });
    }
  };

  return (
    <>
      {currentStorageCategoryInfo ? (
        <Row>
          <Col md="12">
            <Card className="main-card mb-3">
              <Form>
                <CardBody>
                  <Row>
                    <Col md="12">
                      <FormGroup>
                        <Label for="name">Name</Label>
                        <Input
                          invalid={nameErr !== "" ? true : false}
                          type="text"
                          name="name"
                          id="name"
                          placeholder="Name here..."
                          value={
                            currentStorageCategoryInfo.name
                              ? currentStorageCategoryInfo.name
                              : ""
                          }
                          onChange={handleInputChange}
                          onKeyUp={handleValidation}
                        />
                        {nameErr !== "" && (
                          <FormFeedback>{nameErr}</FormFeedback>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <FormGroup>
                      <Label for="storageImage">Image</Label>
                      <Input
                        invalid={imageErr !== "" ? true : false}
                        type="file"
                        name="storageImage"
                        id="storageImage"
                        accept="image/*"
                        onChange={handleFileInput}
                      />
                      {imagePreview && (
                        <div className={styles.previewContainer}>
                          <img
                            width={100}
                            src={imagePreview}
                            alt="preview"
                            onError={() =>
                              setImagePreview(
                                `${process.env.REACT_APP_NO_IMAGE_URL}`
                              )
                            }
                          />
                          <a
                            href="#"
                            className={styles.deleteIcon}
                            onClick={removeProfilePicture}
                          >
                            <i className="pe-7s-trash"></i>
                          </a>
                        </div>
                      )}
                      {imageErr !== "" && (
                        <FormFeedback>{imageErr}</FormFeedback>
                      )}
                    </FormGroup>
                  </Row>
                </CardBody>
                <CardFooter className="d-block">
                  <Button
                    className="me-2"
                    color="link"
                    onClick={() => {
                      navigate(`/storageCategory/list`);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button size="lg" color="primary" onClick={submitHandler}>
                    {JSON.stringify(props.storageCategoryDetail) !== "{}"
                      ? "Update"
                      : "Save"}
                  </Button>
                </CardFooter>
              </Form>
            </Card>
          </Col>
        </Row>
      ) : (
        <Row>
          <Col md="12">
            <Card className="main-card mb-3">
              <p>You are not authorized to access the page!</p>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
};

export default StorageCategoryForm;
